import React, { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import dayjs from "dayjs";
import {
  useGetCalendarDataQuery,
  type ICalendarDay,
} from "../../store/apis/attendance.api";
import { PageHeader, StatsCards } from "../common";
import {
  STAT_CARD_CONFIGS,
  LEGEND_ITEMS,
  WEEKDAYS,
  getStatusCategory,
  getStatusColors,
  getCellStatusText,
} from "../../constants/attendance";

import { parseTime, formatStatValue } from "../../utils/attendanceUtils";

const EmployeeAttendance: React.FC = () => {
  const today = dayjs();
  const [currentMonth, setCurrentMonth] = useState(today.month() + 1);
  const [currentYear, setCurrentYear] = useState(today.year());

  const { data: calendarResponse, isLoading } = useGetCalendarDataQuery({
    month: currentMonth,
    year: currentYear,
  });

  const days = calendarResponse?.data?.days || [];

  const dayMap = useMemo(() => {
    const map = new Map<number, ICalendarDay>();
    days.forEach((d) => map.set(d.day, d));
    return map;
  }, [days]);

  const stats = useMemo(() => {
    let present = 0;
    let absent = 0;
    let leave = 0;
    const presentWithBoth: { in: string; out: string }[] = [];

    for (const d of days) {
      const cat = d.status ? getStatusCategory(d.status) : "none";
      if (cat === "present") {
        present++;
        if (d.checkInTime && d.checkOutTime) {
          presentWithBoth.push({ in: d.checkInTime, out: d.checkOutTime });
        }
      } else if (cat === "absent") {
        absent++;
      } else if (cat === "leave") {
        leave++;
      }
    }

    const avgHours =
      presentWithBoth.length > 0
        ? (
          presentWithBoth.reduce((sum, t) => {
            const h =
              (new Date(t.out).getTime() - new Date(t.in).getTime()) /
              (1000 * 60 * 60);
            return sum + Math.max(0, h);
          }, 0) / presentWithBoth.length
        ).toFixed(1)
        : "0";

    return { present, absent, leave, avgHours };
  }, [days]);

  const goPrevMonth = () => {
    if (currentMonth === 1) {
      setCurrentMonth(12);
      setCurrentYear((y) => y - 1);
    } else {
      setCurrentMonth((m) => m - 1);
    }
  };

  const goNextMonth = () => {
    if (currentMonth === 12) {
      setCurrentMonth(1);
      setCurrentYear((y) => y + 1);
    } else {
      setCurrentMonth((m) => m + 1);
    }
  };

  const monthLabel = dayjs(`${currentYear}-${currentMonth}-01`).format(
    "MMMM YYYY",
  );
  const firstDayOffset = dayjs(
    `${currentYear}-${currentMonth}-01`,
  ).day();
  const daysInMonth = dayjs(
    `${currentYear}-${currentMonth}-01`,
  ).daysInMonth();

  const isTodayCell = (d: number) =>
    d === today.date() &&
    currentMonth === today.month() + 1 &&
    currentYear === today.year();

  const renderCalendarCells = () => {
    const cells: React.ReactNode[] = [];

    for (let i = 0; i < firstDayOffset; i++) {
      cells.push(<div key={`empty-${i}`} />);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const dayData = dayMap.get(d);
      const status = dayData?.status || "";
      const isHoliday = dayData?.isHoliday;
      const holidayText = isHoliday && dayData?.holidayName?.trim() ? dayData.holidayName.trim() : null;
      const colors = getStatusColors(status, isHoliday, holidayText);
      const statusText = getCellStatusText(status, holidayText);
      const showTime = !isHoliday && status.startsWith("P") && dayData?.checkInTime;
      const isToday = isTodayCell(d);
      const isMuted = isHoliday;

      const cellStyle: React.CSSProperties = {
        background: isToday ? "var(--color-attendance-today-bg)" : colors.cellBg,
        border: `1px solid ${isToday ? "var(--color-primary-700)" : colors.cellBorder}`,
        boxShadow: isToday ? `0 0 0 1px var(--color-primary-700)` : undefined,
      };

      const dateColor = isToday
        ? "var(--color-primary-700)"
        : isMuted
          ? "var(--color-attendance-holiday-text)"
          : "var(--color-text-primary)";

      cells.push(
        <div
          key={d}
          className="rounded-[12px] flex flex-col justify-between min-h-[56px] sm:min-h-[66px] lg:min-h-[74px] p-[5px_5px] sm:p-[6px_7px] lg:p-[8px_9px]"
          style={cellStyle}
        >
          <span
            className="font-bold leading-tight text-[11px] sm:text-[12px] lg:text-[13px]"
            style={{ color: dateColor }}
          >
            {d}
          </span>
          <div className="flex items-center gap-1">
            {!isHoliday && status && colors.dot && (
              <span
                className="w-[5px] h-[5px] lg:w-[6px] lg:h-[6px] rounded-full flex-none"
                style={{ background: colors.dot }}
              />
            )}
            {holidayText && (
              <span
                className="inline font-semibold text-[9px] sm:text-[10px] lg:text-[10.5px] leading-none truncate max-w-full"
                style={{ color: colors.timeText }}
              >
                {holidayText}
              </span>
            )}
            {statusText && (
              <span
                className="hidden sm:inline font-semibold text-[9px] sm:text-[10px] lg:text-[10.5px] leading-none"
                style={{ color: colors.timeText }}
              >
                {statusText}
              </span>
            )}
            {showTime && (
              <span
                className="hidden sm:inline font-semibold text-[9px] sm:text-[10px] lg:text-[10.5px] leading-none"
                style={{ color: colors.timeText }}
              >
                {parseTime(dayData?.checkInTime)}
              </span>
            )}
          </div>
        </div>,
      );
    }

    return cells;
  };

  if (isLoading && !days.length) {
    return (
      <div className="w-full space-y-4 sm:space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-[var(--color-bg-skeleton)] rounded" />
        <div className="h-4 w-64 bg-[var(--color-bg-skeleton)] rounded" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[16px] sm:gap-[22px]">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="h-[88px] bg-[var(--color-bg-skeleton)] rounded-[16px]"
            />
          ))}
        </div>
        <div className="h-[300px] sm:h-[400px] bg-[var(--color-bg-skeleton)] rounded-[16px]" />
      </div>
    );
  }

  return (
    <div className="w-full">
      <PageHeader
        title="My Attendance"
        description="Your daily check-in record for the month"
        breadcrumbs={[{ label: "Home", path: "/dashboard" }, { label: "My Attendance" }]}
        className="mb-5 sm:mb-6"
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={goPrevMonth}
              className="w-9 h-9 sm:w-10 sm:h-10 grid place-items-center border border-[var(--color-border-primary)] bg-[var(--color-bg-base)] rounded-[11px] cursor-pointer hover:bg-[var(--color-bg-muted)] transition-colors"
            >
              <ChevronLeft className="w-[15px] h-[15px] sm:w-[17px] sm:h-[17px] text-[var(--color-text-secondary)]" />
            </button>
            <span className="text-sm font-bold text-[var(--color-text-primary)] min-w-[100px] sm:min-w-[108px] text-center">
              {monthLabel}
            </span>
            <button
              onClick={goNextMonth}
              className="w-9 h-9 sm:w-10 sm:h-10 grid place-items-center border border-[var(--color-border-primary)] bg-[var(--color-bg-base)] rounded-[11px] cursor-pointer hover:bg-[var(--color-bg-muted)] transition-colors"
            >
              <ChevronRight className="w-[15px] h-[15px] sm:w-[17px] sm:h-[17px] text-[var(--color-text-secondary)]" />
            </button>
          </div>
        }
      />

      {/* Stat Cards */}
      <div className="mb-5 sm:mb-6">
        <StatsCards
          stats={stats}
          cards={STAT_CARD_CONFIGS.map((c) => ({
            key: c.key,
            label: c.label,
            icon: c.Icon,
            iconBg: c.iconBg,
            iconColor: c.iconColor,
            suffix: c.suffix,
          }))}
          variant="simple-icon"
          gridCols="lg:grid-cols-4"
          valueOnTop
          cardClassName="rounded-[16px] bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] shadow-[var(--shadow-sm)] p-4 sm:p-[22px] transition-all duration-150 hover:-translate-y-[3px] hover:shadow-[var(--shadow-md)]"
          containerClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-[16px] sm:gap-[20px] lg:gap-[22px]"
        />
      </div>

      {/* Calendar View */}
      <div className="rounded-[16px] bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] shadow-[var(--shadow-sm)] p-4 sm:p-[22px]">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-[14px] sm:mb-[18px]">
          <h3 className="text-sm sm:text-base font-bold text-[var(--color-text-primary)]">
            Calendar View
          </h3>
          <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
            {LEGEND_ITEMS.map((item) => (
              <span
                key={item.label}
                className="flex items-center gap-[5px] sm:gap-[6px] text-[10px] sm:text-[11.5px] font-semibold text-[var(--color-text-secondary)]"
              >
                <span
                  className="w-[8px] h-[8px] sm:w-[9px] sm:h-[9px] rounded-full"
                  style={{ background: item.color }}
                />
                {item.label}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-7 gap-[6px] sm:gap-[7px] lg:gap-[9px]">
          {WEEKDAYS.map((day) => (
            <div
              key={day}
              className="text-center text-[10px] sm:text-[11px] font-bold text-[var(--color-text-tertiary)] tracking-[0.04em] pb-[2px]"
            >
              {day}
            </div>
          ))}
          {renderCalendarCells()}
        </div>
      </div>
    </div>
  );
};

export default EmployeeAttendance;
